import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import theme from "../../../themes";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import {
  GetAllMemberLoginCredentialsDocument,
  GetAllMemberLoginCredentialsQuery,
  GetAllMemberLoginCredentialsStatusEnum,
  LoginInfoEmailTypes,
  useCreateAllMemberLoginCredentialsMutation,
  useGetAllMemberLoginCredentialSuccessfulDownloadTimestampUtcMilliQuery,
  useGetAllMemberMassSendLoginCredentialTimestampUtcMilliQuery,
  useGetSampleLoginInfoEmailQuery,
  useMassSendLoginInfoEmailMutation,
} from "../../../graphql/types";
import { useEnumStrings } from "../../enumStrings";
import { getApolloContext } from "@apollo/client";
import { getUserFacingErrorText } from "../../common/utilities/userFacingError";
import { CHAMBER_MEMBER_PRO_DASHBOARD_URL, isChamberMemberProCredentialRefusal } from "./chamberMemberProCredentials";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import { getLocalTimestamp } from "../Root/RootAdmin";
import { CircularProgress, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { SampleLoginInfoEmailModal } from "../../common/SendLoginInformationModal";
import SendCredsIcon from "../../../images/sendcreds.png";

const useStyles = makeStyles(() =>
  createStyles({
    modal: {
      border: "unset",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    // Main modal paper (Figma design)
    mainPaper: {
      overflow: "auto",
      maxHeight: "95vh",
      borderRadius: "20px",
      textAlign: "center",
      width: "950px",
      maxWidth: "95%",
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      backgroundColor: "#FFFFFF",
      color: "inherit",
      padding: "30px 36px",
      paddingTop: "24px",
      position: "relative",
      fontFamily: "DM Sans, sans-serif",
      "@media (max-width: 768px)": {
        padding: "20px 16px",
        width: "100%",
        maxWidth: "95%",
      },
      "@media (max-width: 600px)": {
        borderRadius: "16px",
        padding: "18px 16px",
        paddingTop: "18px",
        maxWidth: "calc(100% - 24px)",
        maxHeight: "calc(100vh - 24px)",
      },
    },
    // Legacy paper for sub-modals
    paper: {
      overflow: "scroll",
      maxHeight: "100%",
      borderRadius: "5px",
      minHeight: "500px",
      textAlign: "center",
      width: "740px",
      maxWidth: "100%",
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      backgroundColor: "#cfd8dc",
      color: "inherit",
      padding: theme.spacing(3),
      paddingTop: theme.spacing(4),
      position: "relative",
      "@media (max-width: 600px)": {
        padding: theme.spacing(2),
        paddingTop: theme.spacing(3),
        minHeight: 0,
        maxHeight: "calc(100vh - 24px)",
      },
    },
    closeButton: {
      position: "absolute",
      right: "14px",
      top: "14px",
      backgroundColor: "#ED1C24",
      color: "white",
      width: "28px",
      height: "28px",
      borderRadius: "50%",
      "&:hover": {
        backgroundColor: "#D41920",
      },
    },
    closeIcon: {
      fontSize: "18px",
    },
    sendCredsIcon: {
      width: "70px",
      height: "70px",
      marginBottom: "14px",
    },
    modalTitle: {
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 700,
      fontSize: "22px",
      color: "#000",
      marginBottom: "10px",
      lineHeight: "1.4",
      textTransform: "uppercase" as const,
    },
    modalDescription: {
      fontFamily: "DM Sans, sans-serif",
      fontSize: "14px",
      color: "#000",
      lineHeight: "150%",
      marginBottom: "8px",
      textAlign: "left" as const,
      width: "100%",
    },
    modalImportantNote: {
      fontFamily: "DM Sans, sans-serif",
      fontSize: "14px",
      color: "#000",
      lineHeight: "150%",
      marginBottom: "20px",
      textAlign: "left" as const,
      width: "100%",
    },
    stackedContainer: {
      border: "1px solid #000000",
      borderRadius: "16px",
      width: "100%",
      textAlign: "left" as const,
    },
    sectionAuto: {
      padding: "24px 28px",
    },
    sectionManual: {
      padding: "20px 28px 24px",
    },
    sectionTitle: {
      fontFamily: "DM Sans, sans-serif",
      fontSize: "18px",
      fontWeight: 700,
      color: "#000",
      marginBottom: "8px",
    },
    sectionBody: {
      fontFamily: "DM Sans, sans-serif",
      fontSize: "14px",
      color: "#000",
      lineHeight: "150%",
      marginBottom: "12px",
    },
    sectionTimestamp: {
      fontFamily: "DM Sans, sans-serif",
      fontSize: "14px",
      color: "#000",
      marginTop: "12px",
      fontWeight: 600,
    },
    previewEmailButton: {
      backgroundColor: "#000000",
      color: "#FFFFFF",
      fontWeight: "bold",
      borderRadius: "12px",
      padding: "10px 28px",
      textTransform: "uppercase" as const,
      fontSize: "12px",
      fontFamily: "DM Sans, sans-serif",
      boxShadow: "none",
      "&:hover": {
        backgroundColor: "#333333",
        boxShadow: "none",
      },
    },
    downloadButton: {
      backgroundColor: theme.palette.primary.main,
      color: "#FFFFFF",
      fontWeight: "bold",
      borderRadius: "12px",
      padding: "10px 28px",
      textTransform: "uppercase" as const,
      fontSize: "12px",
      fontFamily: "DM Sans, sans-serif",
      boxShadow: "none",
      marginLeft: "12px",
      "&:hover": {
        backgroundColor: "#D4A353",
        boxShadow: "none",
      },
    },
    sendEmailButton: {
      backgroundColor: "#00B621",
      color: "#FFFFFF",
      fontWeight: "bold",
      borderRadius: "12px",
      padding: "10px 28px",
      textTransform: "uppercase" as const,
      fontSize: "12px",
      fontFamily: "DM Sans, sans-serif",
      boxShadow: "none",
      marginLeft: "12px",
      "&:hover": {
        backgroundColor: "#009A1C",
        boxShadow: "none",
      },
    },
    sectionButtonsRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap" as const,
      gap: "8px",
    },
    yesButton: {
      minWidth: "200px",
      margin: "10px",
      backgroundColor: "#E1A731",
      color: "#e9e9e9",
      fontWeight: "bold",
      height: "40px",
      textTransform: "none",
    },
    okButton: {
      minWidth: "200px",
      margin: "10px",
      backgroundColor: "#37474f",
      color: "#e9e9e9",
      fontWeight: "bold",
      height: "40px",
      textTransform: "none",
    },
    centeredText: {
      textAlign: "center",
      width: "100%",
    },
    linkText: {
      textDecoration: "underline",
      cursor: "pointer",
      color: "inherit",
    },
    largeTooltip: {
      fontSize: "16px",
      padding: "12px 16px",
    },
  })
);

const DownloadSuccessModal = ({
  open,
  handleClose,
  rawCSVText,
  numOfMembersNotLoggedInYet,
}: {
  open: boolean;
  handleClose: () => void;
  rawCSVText: string;
  numOfMembersNotLoggedInYet: number;
}): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  const handleManualDownloadOfCsv = useCallback(() => {
    if (rawCSVText) {
      const blob = new Blob([rawCSVText], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        enumStrings.CSVDownloadNotification.download,
        enumStrings.CSVDownloadNotification.CredentialsCsv
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [
    enumStrings.CSVDownloadNotification.CredentialsCsv,
    enumStrings.CSVDownloadNotification.download,
    rawCSVText,
  ]);

  useEffect(() => {
    handleManualDownloadOfCsv();
    // empty dependency array cuz only wanna run this once when the modal opens ... bruh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal className={classes.modal} open={open}>
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          border: "1px solid #888888",
          maxWidth: "505px",
          width: "95%",
          padding: "10px 40px 36px",
          textAlign: "center",
          position: "relative",
          fontFamily: "DM Sans, sans-serif",
          outline: "none",
        }}
      >
        <IconButton
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            backgroundColor: "#ED1C24",
            color: "#fff",
            width: "30px",
            height: "30px",
            borderRadius: "100px",
            padding: 0,
          }}
          onClick={handleClose}
        >
          <CloseIcon style={{ fontSize: "18px" }} />
        </IconButton>

        {/* Success checkmark icon */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
          <div
            style={{
              width: "105px",
              height: "105px",
              borderRadius: "50%",
              backgroundColor: "#00B621",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="60"
              height="60"
              viewBox="0 0 60 60"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="30"
                cy="30"
                r="25"
                stroke="#FFFFFF"
                strokeWidth="4"
                fill="none"
              />
              <path
                d="M18 30L26 38L42 22"
                stroke="#FFFFFF"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>
        </div>

        <div style={{ fontWeight: 600, fontSize: "25px", color: "#000", marginBottom: "12px", lineHeight: "200%" }}>
          {enumStrings.CSVDownloadNotification.downloadsuccess}
        </div>

        <div style={{ fontSize: "16px", lineHeight: "200%", color: "#000", marginBottom: "24px", textAlign: "center" }}>
          {enumStrings.CSVDownloadNotification.downloadSuccessDescription}
        </div>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <Button
            style={{
              backgroundColor: "#E1A731",
              color: "#FFFFFF",
              fontFamily: "DM Sans, sans-serif",
              fontWeight: 600,
              fontSize: "16px",
              borderRadius: "12px",
              padding: "15px 96px",
              textTransform: "uppercase",
              boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
            }}
            onClick={handleManualDownloadOfCsv}
            variant="contained"
          >
            {enumStrings.CSVDownloadNotification.download}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const DownloadPendingModal = ({
  open,
  setAllMemberLoginCredentialsRawCSVText,
  setDownloadFailureModalOpen,
  setFailureMessage,
  refetchDownloadTimestamp,
}: {
  open: boolean;
  setAllMemberLoginCredentialsRawCSVText: (value: string) => void;
  setDownloadFailureModalOpen: (value: boolean) => void;
  setFailureMessage: (value: string) => void;
  refetchDownloadTimestamp: () => void;
}) => {
  const classes = useStyles();

  const enumStrings = useEnumStrings();

  const modalOpenTime = useMemo(() => {
    return new Date();
  }, []);

  const apolloClient = useContext(getApolloContext());

  const handleAllMembereLoginCredentialsDownload = useCallback(
    async ({ downloadProcessId }: { downloadProcessId: string }) => {
      // if 15 minutes have passed since the modal opened, close the modal
      if (new Date().getTime() - modalOpenTime.getTime() > 15 * 60 * 1000) {
        setDownloadFailureModalOpen(true);
        return;
      }

      if (downloadProcessId && downloadProcessId.length > 0) {
        const allMemberLoginCredentialsData = (await apolloClient.client?.query(
          {
            query: GetAllMemberLoginCredentialsDocument,
            variables: {
              processId: downloadProcessId,
            },
            fetchPolicy: "network-only",
          }
        )) as {
          data: GetAllMemberLoginCredentialsQuery | undefined;
        };

        if (
          allMemberLoginCredentialsData?.data?.getAllMemberLoginCredentials
            ?.status
        ) {
          const downloadStatus =
            allMemberLoginCredentialsData?.data?.getAllMemberLoginCredentials
              ?.status;

          if (
            downloadStatus === GetAllMemberLoginCredentialsStatusEnum.Processing
          ) {
            // pause for 5 seconds before trying again
            await new Promise((resolve) => setTimeout(resolve, 5000));

            await handleAllMembereLoginCredentialsDownload({
              downloadProcessId,
            });
            return;
          }
          if (
            downloadStatus === GetAllMemberLoginCredentialsStatusEnum.Success
          ) {
            if (
              allMemberLoginCredentialsData?.data?.getAllMemberLoginCredentials
                ?.successCSVRawTextOfAllMemberLoginCredentials
            ) {
              setAllMemberLoginCredentialsRawCSVText(
                allMemberLoginCredentialsData?.data
                  ?.getAllMemberLoginCredentials
                  ?.successCSVRawTextOfAllMemberLoginCredentials
              );
              // Refetch the timestamp to update the UI
              refetchDownloadTimestamp();
            } else {
              setDownloadFailureModalOpen(true);
            }
            return;
          }
          if (
            downloadStatus === GetAllMemberLoginCredentialsStatusEnum.Failure
          ) {
            setDownloadFailureModalOpen(true);
            return;
          }
        }
      }
    },
    [
      apolloClient.client,
      modalOpenTime,
      setAllMemberLoginCredentialsRawCSVText,
      setDownloadFailureModalOpen,
      refetchDownloadTimestamp,
    ]
  );

  const [createAllMemberLoginCredentialsMutation] =
    useCreateAllMemberLoginCredentialsMutation();

  useEffect(() => {
    const executeDownloadAllMemberLoginCredentials = async () => {
      let createRes;
      try {
        createRes = await createAllMemberLoginCredentialsMutation();
      } catch (error) {
        // A refused download (for example a Chamber Member Pro chamber) says why
        // and where to go instead of spinning until the 15-minute timeout.
        setFailureMessage(getUserFacingErrorText(error, enumStrings.downloadFailureModal.message));
        setDownloadFailureModalOpen(true);
        return;
      }
      if (createRes.data?.createAllMemberLoginCredentials?.processId) {
        handleAllMembereLoginCredentialsDownload({
          downloadProcessId:
            createRes.data.createAllMemberLoginCredentials.processId,
        });
      }
    };
    executeDownloadAllMemberLoginCredentials();
    // empty dependency array cuz only wanna run this once when the modal opens ... bruh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal className={classes.modal} open={open}>
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          maxWidth: "480px",
          width: "95%",
          padding: "48px 40px 36px",
          textAlign: "center",
          position: "relative",
          fontFamily: "DM Sans, sans-serif",
          outline: "none",
        }}
      >
        <div style={{ fontWeight: 700, fontSize: "22px", color: "#000", marginBottom: "20px" }}>
          {enumStrings.DownloadMemberCredentials.downloadInProgress}
        </div>

        <div style={{ fontSize: "14px", lineHeight: "1.7", color: "#333", marginBottom: "16px" }}>
          {enumStrings.DownloadMemberCredentials.description}
        </div>

        <div style={{ fontSize: "14px", lineHeight: "1.7", color: "#333", marginBottom: "28px" }}>
          {enumStrings.DownloadMemberCredentials.downloadIn5}
        </div>

        <CircularProgress style={{ color: theme.palette.primary.main }} />
      </div>
    </Modal>
  );
};

const EmailPendingModal = ({
  open,
  setEmailSuccessModalOpen,
  setEmailFailureModalOpen,
  setFailureMessage,
}: {
  open: boolean;
  setEmailSuccessModalOpen: (value: boolean) => void;
  setEmailFailureModalOpen: (value: boolean) => void;
  setFailureMessage: (value: string) => void;
}) => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const [massSendLoginInfoEmail] = useMassSendLoginInfoEmailMutation();

  useEffect(() => {
    const executeMassEmailAllMemberLoginCredentials = async () => {
      try {
        // Get all chamber members who haven't logged in yet
        const { data } = await massSendLoginInfoEmail({
          refetchQueries: [
            "getChamberMembers",
            "getChamber",
            "getAllMemberMassSendLoginCredentialTimestampUtcMilli",
          ],
        });

        if (data?.massSendLoginInfoEmail) {
          setEmailSuccessModalOpen(true);
        } else {
          setEmailFailureModalOpen(true);
        }
      } catch (error) {
        console.error("Error sending mass emails:", error);
        setFailureMessage(getUserFacingErrorText(error, enumStrings.downloadFailureModal.message));
        setEmailFailureModalOpen(true);
      }
    };

    executeMassEmailAllMemberLoginCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal className={classes.modal} open={open}>
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          maxWidth: "480px",
          width: "95%",
          padding: "48px 40px 36px",
          textAlign: "center",
          position: "relative",
          fontFamily: "DM Sans, sans-serif",
          outline: "none",
        }}
      >
        <div style={{ fontWeight: 700, fontSize: "22px", color: "#000", marginBottom: "20px" }}>
          {enumStrings.DownloadMemberCredentials.emailPending.title}
        </div>

        <div style={{ fontSize: "14px", lineHeight: "1.7", color: "#333", marginBottom: "16px" }}>
          {enumStrings.DownloadMemberCredentials.emailPending.description}
        </div>

        <div style={{ fontSize: "14px", lineHeight: "1.7", color: "#333", marginBottom: "28px" }}>
          {enumStrings.DownloadMemberCredentials.description}
        </div>

        <CircularProgress style={{ color: theme.palette.primary.main }} />
      </div>
    </Modal>
  );
};

const EmailSuccessModal = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  return (
    <Modal className={classes.modal} open={open}>
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          maxWidth: "480px",
          width: "95%",
          padding: "48px 40px 36px",
          textAlign: "center",
          position: "relative",
          fontFamily: "DM Sans, sans-serif",
          outline: "none",
        }}
      >
        <IconButton
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            backgroundColor: "#EF4444",
            color: "#fff",
            width: "30px",
            height: "30px",
            padding: 0,
          }}
          onClick={handleClose}
        >
          <CloseIcon style={{ fontSize: "18px" }} />
        </IconButton>

        <div style={{ fontWeight: 700, fontSize: "22px", color: "#000", marginBottom: "20px" }}>
          {enumStrings.DownloadMemberCredentials.emailSuccess.title}
        </div>

        <div style={{ fontSize: "14px", lineHeight: "1.7", color: "#333", marginBottom: "16px", textAlign: "left" }}>
          {enumStrings.DownloadMemberCredentials.emailSuccess.description}
        </div>

        <div style={{ fontSize: "14px", lineHeight: "1.7", color: "#333", marginBottom: "28px", textAlign: "left" }}>
          {enumStrings.DownloadMemberCredentials.emailSuccess.additionalInfo}
        </div>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <Button
            style={{
              backgroundColor: "#000",
              color: "#FFFFFF",
              fontFamily: "DM Sans, sans-serif",
              fontWeight: 700,
              fontSize: "14px",
              borderRadius: "8px",
              padding: "12px 32px",
              textTransform: "none",
              minWidth: "140px",
            }}
            onClick={handleClose}
            variant="contained"
          >
            {enumStrings.CSVDownloadNotification.close}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const EmailConfirmationModal = ({
  open,
  handleClose,
  handleConfirm,
  numOfMembersNotLoggedInYet,
  lastEmailSentTimestamp,
}: {
  open: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
  numOfMembersNotLoggedInYet: number;
  lastEmailSentTimestamp?: number;
}): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  const timeAgoDisplay = getTimeAgoText({
    timestamp: lastEmailSentTimestamp,
    enumStrings,
    lastActionPrefix:
      enumStrings.DownloadMemberCredentials.emailConfirmation.lastSentPrefix,
    lastActionSuffix:
      enumStrings.DownloadMemberCredentials.emailConfirmation.lastSentSuffix,
    neverActionText:
      enumStrings.DownloadMemberCredentials.emailConfirmation.neverSent,
  });

  return (
    <Modal className={classes.modal} open={open} onClose={handleClose}>
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          maxWidth: "480px",
          width: "95%",
          padding: "48px 40px 36px",
          textAlign: "center",
          position: "relative",
          fontFamily: "DM Sans, sans-serif",
          outline: "none",
          overflow: "visible",
        }}
      >
        <IconButton
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            backgroundColor: "#EF4444",
            color: "#fff",
            width: "30px",
            height: "30px",
            padding: 0,
          }}
          onClick={handleClose}
        >
          <CloseIcon style={{ fontSize: "18px" }} />
        </IconButton>

        <div
          style={{
            fontWeight: 700,
            fontSize: "22px",
            color: "#000000",
            marginBottom: "20px",
          }}
        >
          {enumStrings.DownloadMemberCredentials.emailConfirmation.title}
        </div>

        <div
          style={{
            fontSize: "14px",
            lineHeight: "1.7",
            color: "#333",
            marginBottom: "16px",
          }}
        >
          {
            enumStrings.DownloadMemberCredentials.emailConfirmation
              .sendToMembersPrefix
          }{" "}
          <strong>{numOfMembersNotLoggedInYet}</strong>{" "}
          {numOfMembersNotLoggedInYet !== 1
            ? enumStrings.DownloadMemberCredentials.emailConfirmation
              .members
            : enumStrings.DownloadMemberCredentials.emailConfirmation
              .member}{" "}
          {
            enumStrings.DownloadMemberCredentials.emailConfirmation
              .sendToMembersSuffix
          }
        </div>

        <div
          style={{
            fontWeight: 700,
            fontSize: "14px",
            color: "#000",
            marginBottom: "16px",
          }}
        >
          {timeAgoDisplay}
        </div>

        <div
          style={{
            fontSize: "14px",
            lineHeight: "1.7",
            color: "#333",
            marginBottom: "28px",
          }}
        >
          {enumStrings.DownloadMemberCredentials.emailConfirmation.message}
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
          }}
        >
          <Button
            style={{
              backgroundColor: "#000",
              color: "#FFFFFF",
              fontFamily: "DM Sans, sans-serif",
              fontWeight: 700,
              fontSize: "14px",
              borderRadius: "8px",
              padding: "12px 32px",
              textTransform: "none",
              minWidth: "140px",
            }}
            onClick={handleConfirm}
            variant="contained"
          >
            {
              enumStrings.DownloadMemberCredentials.emailConfirmation
                .confirmButton
            }
          </Button>
          <Button
            style={{
              backgroundColor: "transparent",
              color: "#000",
              fontFamily: "DM Sans, sans-serif",
              fontWeight: 700,
              fontSize: "14px",
              borderRadius: "8px",
              padding: "12px 32px",
              textTransform: "none",
              border: "1px solid #000",
              minWidth: "140px",
            }}
            onClick={handleClose}
            variant="contained"
          >
            {
              enumStrings.DownloadMemberCredentials.emailConfirmation
                .cancelButton
            }
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const DownloadConfirmationModal = ({
  open,
  handleClose,
  handleConfirm,
  numOfMembersNotLoggedInYet,
  lastDownloadTimestamp,
  isDownloadButtonDisabled,
  downloadMinutesRemaining,
}: {
  open: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
  numOfMembersNotLoggedInYet: number;
  lastDownloadTimestamp?: number;
  isDownloadButtonDisabled: boolean;
  downloadMinutesRemaining: number;
}): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  const timeAgoDisplay = getTimeAgoText({
    timestamp: lastDownloadTimestamp,
    enumStrings,
    lastActionPrefix:
      enumStrings.DownloadMemberCredentials.downloadConfirmation.lastSentPrefix,
    lastActionSuffix:
      enumStrings.DownloadMemberCredentials.downloadConfirmation.lastSentSuffix,
    neverActionText:
      enumStrings.DownloadMemberCredentials.downloadConfirmation.neverSent,
  });

  return (
    <Modal className={classes.modal} open={open} onClose={handleClose}>
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          maxWidth: "480px",
          width: "95%",
          padding: "48px 40px 36px",
          textAlign: "center",
          position: "relative",
          fontFamily: "DM Sans, sans-serif",
          outline: "none",
        }}
      >
        <IconButton
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            backgroundColor: "#EF4444",
            color: "#fff",
            width: "30px",
            height: "30px",
            padding: 0,
          }}
          onClick={handleClose}
        >
          <CloseIcon style={{ fontSize: "18px" }} />
        </IconButton>

        <div
          style={{
            fontWeight: 700,
            fontSize: "22px",
            color: "#000000",
            marginBottom: "20px",
          }}
        >
          {enumStrings.DownloadMemberCredentials.downloadConfirmation.title}
        </div>

        <div
          style={{
            fontSize: "14px",
            lineHeight: "1.7",
            color: "#333",
            marginBottom: "16px",
          }}
        >
          {
            enumStrings.DownloadMemberCredentials.downloadConfirmation
              .downloadForMembersPrefix
          }{" "}
          <strong>{numOfMembersNotLoggedInYet}</strong>{" "}
          {numOfMembersNotLoggedInYet !== 1
            ? enumStrings.DownloadMemberCredentials.downloadConfirmation
              .members
            : enumStrings.DownloadMemberCredentials.downloadConfirmation
              .member}{" "}
          {
            enumStrings.DownloadMemberCredentials.downloadConfirmation
              .downloadForMembersSuffix
          }
        </div>

        <div
          style={{
            fontWeight: 700,
            fontSize: "14px",
            color: "#000",
            marginBottom: "16px",
          }}
        >
          {timeAgoDisplay}
        </div>

        <div
          style={{
            fontSize: "14px",
            lineHeight: "1.7",
            color: "#333",
            marginBottom: "28px",
          }}
        >
          {
            enumStrings.DownloadMemberCredentials.downloadConfirmation
              .message
          }
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
          }}
        >
          <Tooltip
            title={
              isDownloadButtonDisabled
                ? `${enumStrings.DownloadMemberCredentials.disabledButtonTooltip.waitPrefix} ${downloadMinutesRemaining} ${downloadMinutesRemaining !== 1
                    ? enumStrings.DownloadMemberCredentials.disabledButtonTooltip.minutes
                    : enumStrings.DownloadMemberCredentials.disabledButtonTooltip.minute
                  } ${enumStrings.DownloadMemberCredentials.disabledButtonTooltip.waitSuffix}`
                : ""
            }
            arrow
            placement="top"
            classes={{ tooltip: classes.largeTooltip }}
          >
            <span>
              <Button
                style={{
                  backgroundColor: isDownloadButtonDisabled ? "#9e9e9e" : "#000",
                  color: "#FFFFFF",
                  cursor: isDownloadButtonDisabled ? "not-allowed" : undefined,
                  fontFamily: "DM Sans, sans-serif",
                  fontWeight: 700,
                  fontSize: "14px",
                  borderRadius: "8px",
                  padding: "12px 32px",
                  textTransform: "none",
                  minWidth: "140px",
                }}
                onClick={handleConfirm}
                variant="contained"
                disabled={isDownloadButtonDisabled}
              >
                {
                  enumStrings.DownloadMemberCredentials.downloadConfirmation
                    .confirmButton
                }
              </Button>
            </span>
          </Tooltip>
          <Button
            style={{
              backgroundColor: "transparent",
              color: "#000",
              fontFamily: "DM Sans, sans-serif",
              fontWeight: 700,
              fontSize: "14px",
              borderRadius: "8px",
              padding: "12px 32px",
              textTransform: "none",
              border: "1px solid #000",
              minWidth: "140px",
            }}
            onClick={handleClose}
            variant="contained"
          >
            {
              enumStrings.DownloadMemberCredentials.downloadConfirmation
                .cancelButton
            }
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const DownloadFailureModal = ({
  open,
  handleClose,
  message,
}: {
  open: boolean;
  handleClose: () => void;
  message?: string | null;
}) => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  // A Chamber Member Pro refusal is not a fault: name the right dashboard.
  const chamberMemberPro = isChamberMemberProCredentialRefusal(message);

  return (
    <Modal className={classes.modal} open={open}>
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          maxWidth: "480px",
          width: "95%",
          padding: "48px 40px 36px",
          textAlign: "center",
          position: "relative",
          fontFamily: "DM Sans, sans-serif",
          outline: "none",
        }}
      >
        <IconButton
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            backgroundColor: "#EF4444",
            color: "#fff",
            width: "30px",
            height: "30px",
            padding: 0,
          }}
          onClick={handleClose}
        >
          <CloseIcon style={{ fontSize: "18px" }} />
        </IconButton>

        <div style={{ fontWeight: 700, fontSize: "22px", color: "#000", marginBottom: "20px" }}>
          {chamberMemberPro ? enumStrings.downloadFailureModal.chamberMemberProTitle : enumStrings.downloadFailureModal.title}
        </div>

        <div style={{ fontSize: "14px", lineHeight: "1.7", color: "#333", marginBottom: "28px" }}>
          {message || (
            <>
              {enumStrings.downloadFailureModal.message}
              <br />
              {enumStrings.downloadFailureModal.pleaseContact}{" "}
              {enumStrings.supportContactEmail}{" "}
              {enumStrings.downloadFailureModal.thisIsTheError}
            </>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "12px" }}>
          {chamberMemberPro && (
            <Button
              style={{
                backgroundColor: theme.palette.primary.main,
                color: "#FFFFFF",
                fontFamily: "DM Sans, sans-serif",
                fontWeight: 700,
                fontSize: "14px",
                borderRadius: "8px",
                padding: "12px 24px",
                textTransform: "none",
                minWidth: "140px",
              }}
              href={CHAMBER_MEMBER_PRO_DASHBOARD_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
            >
              {enumStrings.downloadFailureModal.openChamberMemberPro}
            </Button>
          )}
          <Button
            style={{
              backgroundColor: "#000",
              color: "#FFFFFF",
              fontFamily: "DM Sans, sans-serif",
              fontWeight: 700,
              fontSize: "14px",
              borderRadius: "8px",
              padding: "12px 32px",
              textTransform: "none",
              minWidth: "140px",
            }}
            onClick={handleClose}
            variant="contained"
          >
            {enumStrings.downloadFailureModal.closeButtonText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Shared utility function to calculate time ago text
const getTimeAgoText = ({
  timestamp,
  enumStrings,
  lastActionPrefix,
  lastActionSuffix,
  neverActionText,
}: {
  timestamp?: number;
  enumStrings: ReturnType<typeof useEnumStrings>;
  lastActionPrefix: string;
  lastActionSuffix: string;
  neverActionText: string;
}) => {
  if (!timestamp) {
    return neverActionText;
  }

  const now = Date.now();
  const diffInMs = now - timestamp;
  const diffInMinutes = Math.floor(diffInMs / 60000);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  let timeAgoText = "";
  if (diffInDays > 0) {
    timeAgoText = `${diffInDays} ${diffInDays !== 1
        ? enumStrings.DownloadMemberCredentials.timeAgo.days
        : enumStrings.DownloadMemberCredentials.timeAgo.day
      }`;
  } else if (diffInHours > 0) {
    timeAgoText = `${diffInHours} ${diffInHours !== 1
        ? enumStrings.DownloadMemberCredentials.timeAgo.hours
        : enumStrings.DownloadMemberCredentials.timeAgo.hour
      }`;
  } else if (diffInMinutes > 0) {
    timeAgoText = `${diffInMinutes} ${diffInMinutes !== 1
        ? enumStrings.DownloadMemberCredentials.timeAgo.minutes
        : enumStrings.DownloadMemberCredentials.timeAgo.minute
      }`;
  } else {
    timeAgoText =
      enumStrings.DownloadMemberCredentials.timeAgo.lessThanOneMinute;
  }

  return `${lastActionPrefix} ${timeAgoText} ${lastActionSuffix}`;
};

const DownloadAllMemberLoginCredentialsModal = ({
  open,
  handleClose,
  numOfMembersNotLoggedInYet,
  sampleMemberId,
  openDownloadConfirmationOnOpen = false,
}: {
  open: boolean;
  handleClose: () => void;
  numOfMembersNotLoggedInYet: number;
  sampleMemberId?: string;
  openDownloadConfirmationOnOpen?: boolean;
}): ReactElement => {
  const chamberInfo = useGetChamberFromUserIdInAuth();

  const classes = useStyles();
  const enumStrings = useEnumStrings();

  const [downloadSuccessModalOpen, setDownloadSuccessModalOpen] =
    useState(false);

  const [downloadPendingModalOpen, setDownloadPendingModalOpen] =
    useState(false);

  const [downloadFailureModalOpen, setDownloadFailureModalOpen] =
    useState(false);

  const [emailConfirmationModalOpen, setEmailConfirmationModalOpen] =
    useState(false);
  const [emailPendingModalOpen, setEmailPendingModalOpen] = useState(false);

  const [emailSuccessModalOpen, setEmailSuccessModalOpen] = useState(false);

  const [emailFailureModalOpen, setEmailFailureModalOpen] = useState(false);

  // The server's own reason for a refused download or mass email.
  const [failureMessage, setFailureMessage] = useState<string | null>(null);

  const [downloadConfirmationModalOpen, setDownloadConfirmationModalOpen] =
    useState(openDownloadConfirmationOnOpen);

  const [sampleLoginInfoEmailModalOpen, setSampleLoginInfoEmailModalOpen] =
    useState(false);

  const closeSampleLoginInfoEmailModal = useCallback(() => {
    setSampleLoginInfoEmailModalOpen(false);
  }, []);

  const [rawCSVText, setRawCSVText] = useState("");

  const setAllMemberLoginCredentialsRawCSVText = useCallback(
    (value: string) => {
      setRawCSVText(value);
      setDownloadSuccessModalOpen(true);
    },
    [setRawCSVText, setDownloadSuccessModalOpen]
  );

  const {
    data: allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilliData,
    refetch: refetchDownloadTimestamp,
  } = useGetAllMemberLoginCredentialSuccessfulDownloadTimestampUtcMilliQuery({
    variables: {
      chamberId: chamberInfo?.id || "",
    },
    skip: !chamberInfo?.id,
  });

  const {
    data: allMemberMassSendLoginCredentialTimestampUtcMilliData,
    loading: loadingLastEmailTimestamp,
  } = useGetAllMemberMassSendLoginCredentialTimestampUtcMilliQuery({
    variables: {
      chamberId: chamberInfo?.id || "",
    },
    skip: !chamberInfo?.id,
  });

  // Sample email preview functionality
  const { data: getSampleLoginEmail } = useGetSampleLoginInfoEmailQuery({
    variables: {
      type: LoginInfoEmailTypes.BrandNew,
      chamberMemberId: sampleMemberId || "",
    },
    skip: !sampleMemberId,
  });

  // Calculate if email button should be disabled (1 hour cooldown)
  const lastEmailSentTimestamp =
    allMemberMassSendLoginCredentialTimestampUtcMilliData?.getChamber
      ?.allMemberMassSendLoginCredentialTimestampUtcMilli;

  const { isEmailButtonDisabled, minutesRemaining: emailMinutesRemaining } =
    useMemo(() => {
      if (!lastEmailSentTimestamp) {
        return { isEmailButtonDisabled: false, minutesRemaining: 0 };
      }

      const now = Date.now();
      const oneHourInMs = 60 * 60 * 1000;
      const timeSinceLastSend = now - lastEmailSentTimestamp;

      if (timeSinceLastSend < oneHourInMs) {
        const remaining = Math.ceil((oneHourInMs - timeSinceLastSend) / 60000);
        return { isEmailButtonDisabled: true, minutesRemaining: remaining };
      }

      return { isEmailButtonDisabled: false, minutesRemaining: 0 };
    }, [lastEmailSentTimestamp]);

  // Calculate if download button should be disabled (1 hour cooldown)
  const lastDownloadTimestamp =
    allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilliData?.getChamber
      ?.allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilli;

  const {
    isDownloadButtonDisabled,
    minutesRemaining: downloadMinutesRemaining,
  } = useMemo(() => {
    if (!lastDownloadTimestamp) {
      return { isDownloadButtonDisabled: false, minutesRemaining: 0 };
    }

    const now = Date.now();
    const oneHourInMs = 60 * 60 * 1000;
    const timeSinceLastDownload = now - lastDownloadTimestamp;

    if (timeSinceLastDownload < oneHourInMs) {
      const remaining = Math.ceil(
        (oneHourInMs - timeSinceLastDownload) / 60000
      );
      return { isDownloadButtonDisabled: true, minutesRemaining: remaining };
    }

    return { isDownloadButtonDisabled: false, minutesRemaining: 0 };
  }, [lastDownloadTimestamp]);

  return (
    <>
      {downloadPendingModalOpen ? (
        <DownloadPendingModal
          open={downloadPendingModalOpen}
          setAllMemberLoginCredentialsRawCSVText={
            setAllMemberLoginCredentialsRawCSVText
          }
          setDownloadFailureModalOpen={setDownloadFailureModalOpen}
          setFailureMessage={setFailureMessage}
          refetchDownloadTimestamp={refetchDownloadTimestamp}
        />
      ) : null}
      {downloadSuccessModalOpen && rawCSVText && rawCSVText.length > 0 ? (
        <DownloadSuccessModal
          open={downloadSuccessModalOpen}
          handleClose={handleClose}
          rawCSVText={rawCSVText}
          numOfMembersNotLoggedInYet={numOfMembersNotLoggedInYet}
        />
      ) : null}
      {emailConfirmationModalOpen && (
        <EmailConfirmationModal
          open={emailConfirmationModalOpen}
          handleClose={() => setEmailConfirmationModalOpen(false)}
          handleConfirm={() => {
            setEmailConfirmationModalOpen(false);
            setFailureMessage(null);
            setEmailPendingModalOpen(true);
          }}
          numOfMembersNotLoggedInYet={numOfMembersNotLoggedInYet}
          lastEmailSentTimestamp={lastEmailSentTimestamp ?? undefined}
        />
      )}
      {downloadConfirmationModalOpen && (
        <DownloadConfirmationModal
          open={downloadConfirmationModalOpen}
          handleClose={() => {
            if (openDownloadConfirmationOnOpen) {
              handleClose();
              return;
            }

            setDownloadConfirmationModalOpen(false);
          }}
          handleConfirm={() => {
            setDownloadConfirmationModalOpen(false);
            setFailureMessage(null);
            setDownloadPendingModalOpen(true);
          }}
          numOfMembersNotLoggedInYet={numOfMembersNotLoggedInYet}
          lastDownloadTimestamp={lastDownloadTimestamp ?? undefined}
          isDownloadButtonDisabled={isDownloadButtonDisabled}
          downloadMinutesRemaining={downloadMinutesRemaining}
        />
      )}
      {emailPendingModalOpen ? (
        <EmailPendingModal
          open={emailPendingModalOpen}
          setEmailSuccessModalOpen={setEmailSuccessModalOpen}
          setEmailFailureModalOpen={setEmailFailureModalOpen}
          setFailureMessage={setFailureMessage}
        />
      ) : null}
      {emailSuccessModalOpen ? (
        <EmailSuccessModal
          open={emailSuccessModalOpen}
          handleClose={handleClose}
        />
      ) : null}
      <DownloadFailureModal
        open={downloadFailureModalOpen || emailFailureModalOpen}
        handleClose={handleClose}
        message={failureMessage}
      />
      <SampleLoginInfoEmailModal
        open={sampleLoginInfoEmailModalOpen}
        handleClose={closeSampleLoginInfoEmailModal}
        emailHtml={getSampleLoginEmail?.getSampleLoginInfoEmail?.emailHtml}
        modalType={"memberTable"}
        chamberName={chamberInfo?.name || ""}
        chamberEmail={chamberInfo?.supportContactEmail || ""}
        adminContactName={chamberInfo?.supportContactName || ""}
      />
      <Modal
        className={classes.modal}
        style={{
          ...(downloadFailureModalOpen ||
            downloadSuccessModalOpen ||
            downloadPendingModalOpen ||
            downloadConfirmationModalOpen ||
            emailConfirmationModalOpen ||
            emailPendingModalOpen ||
            emailSuccessModalOpen ||
            emailFailureModalOpen ||
            sampleLoginInfoEmailModalOpen
            ? { display: "none" }
            : {}),
        }}
        open={open}
        onClose={handleClose}
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <div className={classes.mainPaper}>
          <IconButton
            className={classes.closeButton}
            onClick={handleClose}
          >
            <CloseIcon className={classes.closeIcon} />
          </IconButton>

          {/* Send Creds Icon */}
          <img
            src={SendCredsIcon}
            alt="Send Credentials"
            className={classes.sendCredsIcon}
          />

          {/* Title */}
          <div className={classes.modalTitle}>
            {enumStrings.DownloadMemberCredentials.mainModal.title}
          </div>

          {/* Description */}
          <div className={classes.modalDescription}>
            {enumStrings.DownloadMemberCredentials.mainModal.emailInstructions}
          </div>

          {/* Important Note */}
          <div className={classes.modalImportantNote}>
            <strong>{enumStrings.DownloadMemberCredentials.mainModal.importantLabel}</strong>{" "}
            {enumStrings.DownloadMemberCredentials.mainModal.importantNote}
          </div>

          {/* Stacked sections container */}
          <div className={classes.stackedContainer}>
            {/* Automatically Mass Email Section */}
            <div className={classes.sectionAuto}>
              <div className={classes.sectionTitle}>
                {enumStrings.DownloadMemberCredentials.mainModal.autoSectionTitle}
              </div>
              <div className={classes.sectionBody}>
                {enumStrings.DownloadMemberCredentials.mainModal.autoSectionDescription}
              </div>
              <div>
                <Button
                  className={classes.previewEmailButton}
                  onClick={() => setSampleLoginInfoEmailModalOpen(true)}
                  variant="contained"
                >
                  {enumStrings.DownloadMemberCredentials.mainModal.previewLink}
                </Button>
              </div>
              <div className={classes.sectionButtonsRow} style={{ marginTop: "12px" }}>
                <Tooltip
                  title={
                    loadingLastEmailTimestamp
                      ? "Loading..."
                      : isEmailButtonDisabled
                        ? `${enumStrings.DownloadMemberCredentials.disabledButtonTooltip.waitPrefix} ${emailMinutesRemaining} ${emailMinutesRemaining !== 1
                            ? enumStrings.DownloadMemberCredentials.disabledButtonTooltip.minutes
                            : enumStrings.DownloadMemberCredentials.disabledButtonTooltip.minute
                          } ${enumStrings.DownloadMemberCredentials.disabledButtonTooltip.waitSuffix}`
                        : ""
                  }
                  arrow
                  placement="top"
                  classes={{ tooltip: classes.largeTooltip }}
                >
                  <span>
                    <Button
                      className={classes.sendEmailButton}
                      onClick={() => setEmailConfirmationModalOpen(true)}
                      variant="contained"
                      disabled={loadingLastEmailTimestamp || isEmailButtonDisabled}
                      style={{
                        marginLeft: 0,
                        ...((loadingLastEmailTimestamp || isEmailButtonDisabled) && {
                          backgroundColor: "#9e9e9e",
                          cursor: "not-allowed",
                        }),
                      }}
                    >
                      {enumStrings.DownloadMemberCredentials.mainModal.sendAutoButton}
                    </Button>
                  </span>
                </Tooltip>
                <span className={classes.sectionTimestamp} style={{ marginTop: 0 }}>
                  {allMemberMassSendLoginCredentialTimestampUtcMilliData?.getChamber
                    ?.allMemberMassSendLoginCredentialTimestampUtcMilli ? (
                    <>
                      {enumStrings.DownloadMemberCredentials.mainModal.lastEmailSent}{" "}
                      {getLocalTimestamp(
                        allMemberMassSendLoginCredentialTimestampUtcMilliData
                          .getChamber
                          .allMemberMassSendLoginCredentialTimestampUtcMilli,
                        true
                      )}
                    </>
                  ) : null}
                </span>
              </div>
            </div>

            {/* Manually Mass Email Section */}
            <div className={classes.sectionManual}>
              <div className={classes.sectionTitle}>
                {enumStrings.DownloadMemberCredentials.mainModal.manualSectionTitle}
              </div>
              <div className={classes.sectionBody}>
                {enumStrings.DownloadMemberCredentials.mainModal.manualSectionDescriptionLine1}
              </div>
              <div className={classes.sectionBody}>
                {enumStrings.DownloadMemberCredentials.mainModal.manualSectionDescriptionLine2}
              </div>
              <div className={classes.sectionButtonsRow}>
                <Tooltip
                  title={
                    isDownloadButtonDisabled
                      ? `${enumStrings.DownloadMemberCredentials.disabledButtonTooltip.waitPrefix} ${downloadMinutesRemaining} ${downloadMinutesRemaining !== 1
                          ? enumStrings.DownloadMemberCredentials.disabledButtonTooltip.minutes
                          : enumStrings.DownloadMemberCredentials.disabledButtonTooltip.minute
                        } ${enumStrings.DownloadMemberCredentials.disabledButtonTooltip.waitSuffix}`
                      : ""
                  }
                  arrow
                  placement="top"
                  classes={{ tooltip: classes.largeTooltip }}
                >
                  <span>
                    <Button
                      className={classes.downloadButton}
                      onClick={() => setDownloadConfirmationModalOpen(true)}
                      variant="contained"
                      disabled={isDownloadButtonDisabled}
                      style={{
                        marginLeft: 0,
                        ...(isDownloadButtonDisabled && {
                          backgroundColor: "#9e9e9e",
                          cursor: "not-allowed",
                        }),
                      }}
                    >
                      {enumStrings.DownloadMemberCredentials.mainModal.downloadButton}
                    </Button>
                  </span>
                </Tooltip>
                <span className={classes.sectionTimestamp} style={{ marginTop: 0 }}>
                  {allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilliData
                    ?.getChamber
                    ?.allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilli ? (
                    <>
                      {enumStrings.DownloadMemberCredentials.mainModal.lastDownloaded}{" "}
                      {getLocalTimestamp(
                        allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilliData
                          .getChamber
                          .allMemberLoginCredentialSuccessfulDownloadTimestampUtcMilli,
                        true
                      )}
                    </>
                  ) : null}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DownloadAllMemberLoginCredentialsModal;
